import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Task, TaskPriority } from "@common/types/task";
import { TaskPrioritySelect } from "@components/tasks/TaskPrioritySelect/TaskPrioritySelect";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useUpdateTask } from "@api/task.api";
import { TaskPriority as TaskPriorityComponent } from "@components/tasks/TaskPrioritySelect/TaskPriority";
import { TASK_PRIORITY_CONFIG } from "@components/tasks/TaskPrioritySelect/taskPriority.constant";
import { Picker } from "@design-system/Picker/Picker.lib";
import { memo, useMemo } from "react";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { NestedTaskRow } from "../../types";

export const PriorityCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, NestedTaskRow, TaskPriority | null>) => {
        const task = row.original as Task;
        const updateTask = useUpdateTask(task);

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    addText="Set priority"
                    hideAddIcon
                    value={
                        task.priority ? (
                            <TaskPriorityComponent
                                priority={task.priority}
                                variant="iconOnly"
                            />
                        ) : undefined
                    }
                    aria-label={
                        task.priority
                            ? TASK_PRIORITY_CONFIG[task.priority].label
                            : undefined
                    }
                />
            ),
            [task.priority],
        );

        if (row.original.isGroup || row.original.isNewPlaceholder) {
            return null;
        }

        return (
            <ViewTableCell>
                <CaptureClicks>
                    <TaskPrioritySelect
                        value={task.priority ?? null}
                        onValueChange={(priority) =>
                            updateTask.mutate({ priority })
                        }
                        trigger={trigger}
                    />
                </CaptureClicks>
            </ViewTableCell>
        );
    },
);
